import React from "react";

// Small art-deco divider used above/under section titles
export const Ornament = ({ className = "" }) => (
  <div className={`flex items-center justify-center gap-4 ${className}`} aria-hidden>
    <span className="h-px w-16 bg-[var(--lu-gold)]" />
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z"
        fill="var(--lu-gold)"
      />
    </svg>
    <span className="h-px w-16 bg-[var(--lu-gold)]" />
  </div>
);

// Editorial section header — bigger, more breathing room
export const SectionHeader = ({ kicker, title, italicTitle, sub, className = "", light = false }) => (
  <div className={`text-center max-w-3xl mx-auto ${className}`}>
    <p
      className={`kicker ${light ? "text-[var(--lu-gold)]" : "text-[var(--lu-gold-dark)]"}`}
    >
      {kicker}
    </p>
    <h2
      className={`mt-6 h-editorial text-[46px] sm:text-[60px] lg:text-[78px] ${
        light ? "text-[var(--lu-ivory)]" : "text-[var(--lu-black)]"
      }`}
    >
      {title}
      {italicTitle && (
        <>
          {" "}
          <span
            className={`italic ${light ? "text-[var(--lu-gold)]" : "text-[var(--lu-gold-dark)]"}`}
          >
            {italicTitle}
          </span>
        </>
      )}
    </h2>
    <Ornament className="mt-10" />
    {sub && (
      <p
        className={`mt-8 text-[16px] leading-[1.9] font-light ${
          light ? "text-ivory-75" : "text-black-70"
        }`}
      >
        {sub}
      </p>
    )}
  </div>
);

export const SunburstFan = ({ className = "", style = {} }) => (
  <svg
    className={className}
    viewBox="0 0 240 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    style={style}
  >
    {Array.from({ length: 21 }).map((_, i) => {
      const angle = (i / 20) * 180;
      const x2 = 120 + 110 * Math.cos((angle * Math.PI) / 180);
      const y2 = 120 - 110 * Math.sin((angle * Math.PI) / 180);
      return (
        <line
          key={i}
          x1="120"
          y1="120"
          x2={x2}
          y2={y2}
          stroke="var(--lu-gold)"
          strokeWidth={i % 3 === 0 ? "1.2" : "0.6"}
          opacity={i % 2 === 0 ? 0.9 : 0.55}
        />
      );
    })}
    <circle cx="120" cy="120" r="6" fill="var(--lu-gold)" />
  </svg>
);

// Simple deco corner marks
export const CornerFrame = () => (
  <>
    <div className="pointer-events-none absolute inset-6 border border-[var(--lu-gold)]/40" />
    <div className="pointer-events-none absolute top-4 left-4 w-6 h-6 border-t border-l border-[var(--lu-gold)]" />
    <div className="pointer-events-none absolute top-4 right-4 w-6 h-6 border-t border-r border-[var(--lu-gold)]" />
    <div className="pointer-events-none absolute bottom-4 left-4 w-6 h-6 border-b border-l border-[var(--lu-gold)]" />
    <div className="pointer-events-none absolute bottom-4 right-4 w-6 h-6 border-b border-r border-[var(--lu-gold)]" />
  </>
);

// Reveal wrapper — reveals content on scroll AND immediately reveals anything
// that is already in the viewport on mount. Has multiple safety fallbacks so
// content can never stay stuck at opacity: 0 (which previously caused blank
// pages when the IntersectionObserver did not fire).
export const Reveal = ({ as: Tag = "div", delay = 0, className = "", children, ...rest }) => {
  const [ref, setRef] = React.useState(null);

  React.useEffect(() => {
    if (!ref) return;

    // 1) If the user prefers reduced motion, reveal immediately.
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      ref.classList.add("is-in");
      return;
    }

    // 2) If the element is already inside the viewport at mount, reveal it
    //    synchronously — no need to wait for IntersectionObserver.
    try {
      const rect = ref.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const inView = rect.top < vh - 40 && rect.bottom > 0;
      if (inView) {
        ref.classList.add("is-in");
        return;
      }
    } catch (_) {
      /* fall through to observer */
    }

    // 3) Attach IntersectionObserver for content further down the page.
    let disconnected = false;
    let io;
    if (typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            ref.classList.add("is-in");
            io && io.disconnect();
            disconnected = true;
          }
        },
        { threshold: 0.05, rootMargin: "0px 0px -20px 0px" }
      );
      io.observe(ref);
    } else {
      // Very old browsers: just reveal immediately.
      ref.classList.add("is-in");
    }

    // 4) Ultimate safety net — after 1200ms, force reveal so nothing
    //    can ever be stuck invisible for the user.
    const timer = setTimeout(() => {
      if (!disconnected && ref) ref.classList.add("is-in");
    }, 1200);

    return () => {
      clearTimeout(timer);
      io && io.disconnect();
    };
  }, [ref]);

  const delayClass = delay ? `reveal-delay-${delay}` : "";
  return (
    <Tag
      ref={setRef}
      className={`reveal ${delayClass} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
};
